import { useState } from "react";
import { ModalShell } from "../../ModalShell";
import dialogStyles from "../../../pages/admin/AdminFeatureFlags.module.css";
import { VideoEmbed, videoThumbnailUrl } from "../resources/VideoEmbed";
import type { ResourceView } from "../../../hooks/useResources";
import connect from "./connect.module.css";
import styles from "./IntegrationGuide.module.css";

type Props = {
  readonly resource: ResourceView;
  readonly integrationTitle: string;
};

// A touch wider than the "md" preset so the 16:9 embed has room to breathe.
const MODAL_WIDTH = "min(760px, 94vw)";

const formatDuration = (seconds: number | null): string | null => {
  if (seconds === null || seconds <= 0) return null;
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  return `${mm}:${ss.toString().padStart(2, "0")}`;
};

const PlayGlyph = (): JSX.Element => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none" aria-hidden>
    <path d="M5 3.5v11l9-5.5-9-5.5z" fill="currentColor" />
  </svg>
);

// An admin-pinned walkthrough for one integration's connect flow, shown inline
// above the Connect button. Exists for the connects that need more than the
// standard OAuth hop — Meta Ads, where the customer builds their own app first.
// Rendered only when a published resource names this integration.
export const IntegrationGuide = ({ resource, integrationTitle }: Props): JSX.Element => {
  const [open, setOpen] = useState<boolean>(false);
  const titleId = "integration-guide-dialog-title";
  const duration = formatDuration(resource.durationSeconds);
  const { videoProvider, videoId } = resource;
  const playable = videoProvider !== null && videoId !== null;
  // Admin-set thumbnail wins; otherwise derive the provider's. Neither is
  // guaranteed, so the tile falls back to a plain panel.
  const thumbnail =
    resource.thumbnailUrl ??
    (playable ? videoThumbnailUrl(videoProvider, videoId) : null);

  return (
    <div className={styles.guide}>
      <span className={connect.ctaEyebrow}>// GUIDE</span>
      <button
        type="button"
        className={styles.tile}
        onClick={() => setOpen(true)}
        disabled={!playable}
      >
        <span className={styles.thumbWrap}>
          {thumbnail !== null ? (
            <img className={styles.thumb} src={thumbnail} alt="" loading="lazy" />
          ) : (
            <span className={styles.thumbFallback} aria-hidden />
          )}
          <span className={styles.playBadge} aria-hidden>
            <PlayGlyph />
          </span>
        </span>
        <span className={styles.meta}>
          <span className={styles.title}>{resource.title}</span>
          {resource.description !== null ? (
            <span className={styles.description}>{resource.description}</span>
          ) : null}
          {duration !== null ? <span className={styles.duration}>{duration}</span> : null}
        </span>
      </button>

      {open && playable ? (
        <ModalShell onClose={() => setOpen(false)} titleId={titleId} size="md" width={MODAL_WIDTH}>
          <header className={dialogStyles.dialogHeader}>
            <h3 className={dialogStyles.dialogTitle} id={titleId}>
              {resource.title}
            </h3>
            <button
              type="button"
              className={dialogStyles.closeBtn}
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
          </header>
          <div className={styles.modalBody}>
            <VideoEmbed
              provider={videoProvider}
              videoId={videoId}
              title={`How to connect ${integrationTitle}`}
            />
          </div>
        </ModalShell>
      ) : null}
    </div>
  );
};
